import { useEffect, useState } from 'react';
import {
    Snackbar,
    Paper,
    Box,
    Typography,
    Button,
    IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { onMessage } from 'firebase/messaging';
import { messagingReady } from '../providers/firebase/firebase';
import { ALERT_SEVERITY_COLORS } from '../pages/markets/utils/colors';
import type { AlertSeverity } from '../pages/markets/api/types';
import { openInNewTab } from '../utils/newTab';

interface ToastPayload {
    title: string;
    body: string;
    eventId: string;
    severity: AlertSeverity;
}

/**
 * Surfaces FCM pushes that arrive while the app tab is FOCUSED. Background /
 * closed-tab pushes are shown as native OS notifications by the service
 * worker; the SDK delivers foreground messages to `onMessage` instead (the SW
 * never fires), so without this they'd be silent. The bell badge updates
 * independently via the Firestore listener.
 */
export function NotificationToaster({ sx }: { sx?: SxProps<Theme> }) {
    const theme = useTheme();
    const [toast, setToast] = useState<ToastPayload | null>(null);

    useEffect(() => {
        let cancelled = false;
        let unsub = () => {};
        // messaging initialises asynchronously (isSupported probes IndexedDB),
        // so await the ready promise instead of reading it synchronously — a
        // sync read at mount can race the init and silently never attach.
        messagingReady.then(messaging => {
            if (cancelled || !messaging) return;
            unsub = onMessage(messaging, payload => {
                // Pushes are data-only (see functions/notifications/fcm.js), so
                // title/body come from `data`, not a `notification` payload.
                const data = payload.data ?? {};
                setToast({
                    title: data.title ?? 'New alert',
                    body: data.body ?? '',
                    eventId: data.event_id ?? '',
                    severity: (data.severity as AlertSeverity) ?? 'info',
                });
            });
        });
        return () => {
            cancelled = true;
            unsub();
        };
    }, []);

    const handleClose = () => setToast(null);

    const handleView = () => {
        if (toast?.eventId) openInNewTab(`/markets/event/${toast.eventId}`);
        setToast(null);
    };

    // ALERT_SEVERITY_COLORS.log is '' (resolved from the theme at runtime), so
    // fall back to text.secondary — `||` (not `??`) to catch the empty string.
    const borderColor =
        (toast && ALERT_SEVERITY_COLORS[toast.severity]) ||
        theme.palette.text.secondary;

    return (
        <Snackbar
            open={!!toast}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            autoHideDuration={6000}
            onClose={(_e, reason) => {
                if (reason !== 'clickaway') handleClose();
            }}
            sx={[{ mt: 7 }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            <Paper
                elevation={6}
                sx={{
                    width: 340,
                    borderLeft: `4px solid ${borderColor}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1.5,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                        {toast?.title}
                    </Typography>
                    {toast?.body && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                            noWrap
                        >
                            {toast.body}
                        </Typography>
                    )}
                    {toast?.eventId && (
                        <Button
                            size="small"
                            onClick={handleView}
                            sx={{ mt: 0.5, px: 0, minWidth: 0 }}
                        >
                            View
                        </Button>
                    )}
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{ mt: -0.5, mr: -0.5 }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Paper>
        </Snackbar>
    );
}
